import { useEffect, useRef, useState } from 'react'

import { api, downloadBlob } from '../../api/client'
import type { MedFile } from '../../api/types'
import { formatBytes } from '../../attributes'
import { useAuth } from '../../auth/AuthContext'
import { livelloModulo } from '../../auth/permessi'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable } from '../DataTable'

/**
 * CENSO MED (D6): photos and documents of a device and of its model.
 *
 * Every photo is re-encoded here, on a canvas, before it leaves the browser:
 * what reaches the Control Plane has no EXIF and no GPS, and is at most 1600
 * px on its longer side; its 320 px thumbnail goes with it (the Control Plane
 * has no image library). PDFs travel as they are. The files are fetched with
 * the session token (an <img src> could not send it) and shown as object URLs.
 */

const LATO = 1600
const LATO_MINIATURA = 320

/** The photo redrawn on a canvas as JPEG: no metadata survives. */
export async function ricodifica(file: Blob, lato: number, qualita = 0.85): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  const scala = Math.min(1, lato / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(bitmap.width * scala))
  canvas.height = Math.max(1, Math.round(bitmap.height * scala))
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error(t('Il browser non riesce a elaborare l’immagine.'))
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()
  return new Promise((ok, ko) => canvas.toBlob((b) => (b ? ok(b) : ko(new Error(t('Il browser non riesce a elaborare l’immagine.')))),
    'image/jpeg', qualita))
}

/** Upload a photo (re-encoded, plus its thumbnail) or a PDF. */
export async function caricaFile(
  file: File,
  invia: (b: Blob, q: { kind: 'photo' | 'document'; name: string; thumbOf?: string }) => Promise<{ id: string }>,
): Promise<void> {
  if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
    await invia(file, { kind: 'document', name: file.name })
    return
  }
  const foto = await ricodifica(file, LATO)
  const miniatura = await ricodifica(file, LATO_MINIATURA, 0.8)
  const nome = file.name.replace(/\.[^.]+$/, '') + '.jpg'
  const { id } = await invia(foto, { kind: 'photo', name: nome })
  await invia(miniatura, { kind: 'photo', name: nome, thumbOf: id })
}

/** An image fetched with the session token. */
function Immagine({ fileId, miniatura, alt }: { fileId: string; miniatura: boolean; alt: string }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let vivo = true
    let creato: string | null = null
    downloadBlob(api.medFilePath(fileId, miniatura ? 'thumb' : 'full')).then((b) => {
      if (!vivo) return
      creato = URL.createObjectURL(b)
      setUrl(creato)
    }).catch(() => undefined)
    return () => {
      vivo = false
      if (creato) URL.revokeObjectURL(creato)
    }
  }, [fileId, miniatura])
  return url ? <img src={url} alt={alt} className="med-foto__img" /> : <span className="med-foto__vuota" aria-hidden="true" />
}

async function apri(f: MedFile) {
  const b = await downloadBlob(api.medFilePath(f.id))
  const url = URL.createObjectURL(b)
  window.open(url, '_blank', 'noopener')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

function Galleria({ foto, scrive, onCambiato }: { foto: MedFile[]; scrive: boolean; onCambiato: () => void }) {
  const [errore, setErrore] = useState<unknown>(null)
  async function esegui(azione: () => Promise<unknown>) {
    setErrore(null)
    try {
      await azione()
      onCambiato()
    } catch (e) {
      setErrore(e)
    }
  }
  if (!foto.length) return <p className="text-muted" style={{ margin: 0 }}>{t('Nessuna foto.')}</p>
  return (
    <>
      <ErrorBanner error={errore} />
      <ul className="med-foto">
        {foto.map((f) => (
          <li key={f.id} className="med-foto__voce">
            <button type="button" className="med-foto__apri" onClick={() => void apri(f)} title={t('Apri a dimensione intera')}>
              <Immagine fileId={f.id} miniatura={f.has_thumbnail} alt={f.caption || f.name} />
            </button>
            <span className="med-foto__didascalia">{f.caption || f.name}</span>
            {scrive && (
              <span className="med-foto__azioni">
                <button type="button" className="linklike" onClick={() => {
                  const testo = window.prompt(t('Didascalia'), f.caption)
                  if (testo !== null) void esegui(() => api.medFileCaption(f.id, testo))
                }}>{t('Didascalia')}</button>
                <button type="button" className="linklike" onClick={() => {
                  if (window.confirm(t('Togliere questa foto?'))) void esegui(() => api.medFileDelete(f.id))
                }}>{t('Togli')}</button>
              </span>
            )}
          </li>
        ))}
      </ul>
    </>
  )
}

function Caricatore({ etichetta, accetta, invia, onFatto, disabilitato }: {
  etichetta: string
  accetta: string
  invia: (b: Blob, q: { kind: 'photo' | 'document'; name: string; thumbOf?: string }) => Promise<{ id: string }>
  onFatto: () => void
  disabilitato?: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  async function carica(files: FileList) {
    setErrore(null)
    setLavoro(true)
    try {
      for (const f of Array.from(files)) await caricaFile(f, invia)
      onFatto()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
      if (input.current) input.current.value = ''
    }
  }
  return (
    <>
      <input ref={input} type="file" accept={accetta} multiple style={{ display: 'none' }}
             onChange={(e) => { if (e.target.files?.length) void carica(e.target.files) }} />
      <button type="button" className="secondary" onClick={() => input.current?.click()} disabled={lavoro || disabilitato}>
        {lavoro ? t('Carico…') : etichetta}
      </button>
      <ErrorBanner error={errore} />
    </>
  )
}

export function FotoDocumenti({ deviceId }: { deviceId: string }) {
  const { principal } = useAuth()
  const scrive = livelloModulo(principal, 'medical') === 'write'
  const dati = useAsync(() => api.medDeviceFiles(deviceId), [deviceId])
  const x = dati.data
  if (dati.loading && !x) return <Spinner />
  if (!x) return <ErrorBanner error={dati.error} />
  const fotoDispositivo = x.device.filter((f) => f.kind === 'photo')
  const documenti = x.device.filter((f) => f.kind === 'document')
  const ricarica = () => void dati.reload()
  return (
    <>
      <p className="field__hint">
        {t('Le foto non devono mostrare pazienti né dati personali. Il browser le ridisegna prima di inviarle: niente dati EXIF né posizione GPS. Foto fino a 4 MiB, PDF fino a 10 MiB; spazio usato {u} di {q}.', {
          u: formatBytes(x.used_bytes), q: formatBytes(x.quota_bytes) })}
      </p>
      <Card title={t('Foto del dispositivo')} actions={scrive ? (
        <Caricatore etichetta={t('Aggiungi foto…')} accetta="image/*"
          disabilitato={fotoDispositivo.length >= x.limits.photos_per_device}
          invia={(b, q) => api.medDeviceFileUpload(deviceId, b, q)} onFatto={ricarica} />
      ) : undefined}>
        <Galleria foto={fotoDispositivo} scrive={scrive} onCambiato={ricarica} />
      </Card>
      {x.model_key && (
        <Card title={t('Foto del modello')} actions={scrive ? (
          <Caricatore etichetta={t('Aggiungi foto del modello…')} accetta="image/*"
            disabilitato={x.model.length >= x.limits.photos_per_model}
            invia={(b, q) => api.medModelFileUpload(x.model_key!, b, q)} onFatto={ricarica} />
        ) : undefined}>
          <p className="field__hint" style={{ marginTop: 0 }}>{t('Valgono per tutti i dispositivi dello stesso modello in questo ospedale.')}</p>
          <Galleria foto={x.model} scrive={scrive} onCambiato={ricarica} />
        </Card>
      )}
      <Card title={t('Documenti')} actions={scrive ? (
        <Caricatore etichetta={t('Aggiungi PDF…')} accetta="application/pdf"
          disabilitato={documenti.length >= x.limits.documents_per_device}
          invia={(b, q) => api.medDeviceFileUpload(deviceId, b, q)} onFatto={ricarica} />
      ) : undefined}>
        <DataTable
          rows={documenti}
          rowKey={(f) => f.id}
          paginazione={false}
          strumenti={false}
          emptyText={t('Nessun documento: manuale, dichiarazione di conformità, rapporti.')}
          columns={[
            { key: 'name', header: t('Nome'), value: (f) => f.name,
              render: (f) => <button type="button" className="linklike" onClick={() => void apri(f)}>{f.name || t('documento')}</button> },
            { key: 'caption', header: t('Descrizione'), value: (f) => f.caption },
            { key: 'size', header: t('Dimensione'), type: 'number', align: 'right', value: (f) => f.size, render: (f) => formatBytes(f.size) },
            { key: 'created_at', header: t('Caricato il'), type: 'date', value: (f) => f.created_at ?? '', render: (f) => formatDateTime(f.created_at) },
            ...(scrive ? [{
              key: 'azione', header: '', sort: false, searchable: false,
              render: (f: MedFile) => (
                <button type="button" className="secondary" onClick={() => {
                  if (window.confirm(t('Togliere «{n}»?', { n: f.name }))) void api.medFileDelete(f.id).then(ricarica)
                }}>{t('Togli')}</button>
              ),
            }] : []),
          ]}
        />
      </Card>
    </>
  )
}
